/**
 * Call states
 * Call states used in the cpaas and link call plugins
 * @name CALL_STATES
 */
export const CALL_STATES = {
  INITIATING: 'Initiating',
  INITIATED: 'Initiated',
  EARLY_MEDIA: 'Early Media',
  RINGING: 'Ringing',
  CANCELLED: 'Cancelled',
  CONNECTED: 'Connected',
  ON_HOLD: 'On Hold',
  ENDED: 'Ended'
}

/**
 * Possible states a call's media can be in.
 * Describes whether media is flowing or if an endpoint is preventing media flow.
 * @name CALL_MEDIA_STATES
 * @type {Object}
 * @property {string} CONNECTED Both call endpoints allow for media to flow.
 * @property {string} LOCAL_HOLD Local endpoint disallows media from flowing.
 * @property {string} REMOTE_HOLD Remote endpoint disallows media from flowing.
 * @property {string} DUAL_HOLD Both endpoints disallow media from flowing.
 */
export const CALL_MEDIA_STATES = {
  CONNECTED: 'Connected',
  LOCAL_HOLD: 'Local Hold',
  REMOTE_HOLD: 'Remote Hold',
  DUAL_HOLD: 'Dual Hold'
}

/**
 * Possible states for a Call's media connection.
 * @name CALL_MEDIA_CONNECTION_STATES
 */
export const CALL_MEDIA_CONNECTION_STATES = {
  NEW: 'new',
  CHECKING: 'checking',
  CONNECTED: 'connected',
  COMPLETED: 'completed',
  FAILED: 'failed',
  DISCONNECTED: 'disconnected',
  CLOSED: 'closed'
}

/**
 * Call operations that require negotiation.
 * @name OPERATIONS
 * @type {Object}
 * @property {string} CHANGE_MEDIA Media flow remains the same, includes non-flow related media changes.
 * @property {string} HOLD_MEDIA   Media flow stops. May include non-flow related media changes.
 * @property {string} UNHOLD_MEDIA Media flow restarts. May include non-flow related media changes.
 * @property {string} MUSIC_ON_HOLD Media flow changes to sendonly.
 */
export const OPERATIONS = {
  CHANGE_MEDIA: 'Change Media',
  HOLD_MEDIA: 'Hold Media',
  UNHOLD_MEDIA: 'Unhold Media',
  MUSIC_ON_HOLD: 'Music on hold'
}

/**
 * Complex call operations.
 * @name COMPLEX_OPERATIONS
 * @type {Object}
 * @property {string} DIRECT_TRANSFER
 * @property {string} CONSULTATIVE_TRANSFER
 * @property {string} JOIN
 */
export const COMPLEX_OPERATIONS = {
  DIRECT_TRANSFER: 'Direct Transfer',
  CONSULTATIVE_TRANSFER: 'Consultative Transfer',
  JOIN: 'Join'
}

/**
 * Complex call operation messages.
 * @name COMPLEX_OPERATION_MESSAGES
 * @type {Object}
 * @property {string} DIRECT_TRANSFER_SUCCESS Success message for direct transfer.
 * @property {string} CONSULTATIVE_TRANSFER_SUCCESS Success message for consultative transfer (matches reasonText from notification).
 * @property {string} JOIN_SUCCESS Success message for join.
 * @property {string} DIRECT_TRANSFER_FAILURE Failure message for direct transfer.
 * @property {string} CONSULTATIVE_TRANSFER_FAILURE Failure message for consultative transfer.
 * @property {string} JOIN_FAILURE Failure message for join.
 */
export const COMPLEX_OPERATION_MESSAGES = {
  DIRECT_TRANSFER_SUCCESS: 'Direct Transfer Successful',
  CONSULTATIVE_TRANSFER_SUCCESS: 'Transfer Successful',
  JOIN_SUCCESS: 'Join Successful',
  DIRECT_TRANSFER_FAILURE: 'Direct Transfer Failed',
  CONSULTATIVE_TRANSFER_FAILURE: 'Transfer Failed',
  JOIN_FAILURE: 'Join Failed'
}

/**
 * Status codes that match success messages.
 * @name STATUS_CODES
 * @type {Object}
 * @property {number} DIRECT_TRANSFER_SUCCESS Status code for successful direct transfer.
 * @property {number} CONSULTATIVE_TRANSFER_SUCCESS Status code for successful consultative transfer.
 * @property {number} JOIN_SUCCESS Status code for successful join.
 * @property {number} DIRECT_TRANSFER_FAILURE Status code for failed direct transfer.
 * @property {number} CONSULTATIVE_TRANSFER_FAILURE Status code for failed consultative transfer.
 * @property {number} JOIN_FAILURE Status code for failed join.
 */
export const STATUS_CODES = {
  DIRECT_TRANSFER_SUCCESS: 9905,
  CONSULTATIVE_TRANSFER_SUCCESS: 9905,
  JOIN_SUCCESS: 9906,
  DIRECT_TRANSFER_FAILURE: -1,
  CONSULTATIVE_TRANSFER_FAILURE: -1,
  JOIN_FAILURE: -2
}

/**
 * Call direction
 * Indication of which direction the call is going
 * @name CALL_DIRECTION
 */
export const CALL_DIRECTION = {
  INCOMING: 'incoming',
  OUTGOING: 'outgoing'
}

/*
 * A conversion from MediaDeviceInfo.kind values to their more common terms.
 * See: https://developer.mozilla.org/en-US/docs/Web/API/MediaDeviceInfo/kind
 */
export const WEBRTC_DEVICE_KINDS = {
  audioinput: 'microphone',
  videoinput: 'camera',
  audiooutput: 'speaker'
}

/**
 * Result types for the Ice Collection Check Function.
 * @name ICE_COLLECTION_RESULT_TYPES
 */
export const ICE_COLLECTION_RESULT_TYPES = {
  START_CALL: 'StartCall',
  WAIT: 'Wait',
  ERROR: 'Error'
}
